import { Injectable } from '@angular/core';
import { StateDataMapper } from '../../storage/mappers/dispatched-service-state-data-mapper';
import { StoredStateData } from '../../storage/models/stored-state-data';
import { DispatchedServiceViewModel } from '../../models/view-models/dispatched-service.view-model';
import { map, Observable, switchMap } from 'rxjs';
import { GetAllStateDataOperation } from 'src/app/driver-data/operations/state/get-all-state-data-operation';
import { GetAllCachedServicesOperation } from 'src/app/driver-data/operations/cached-services/get-all-cached-services-operation';
import { StoredCachedService } from '../../storage/models/stored-cached-service';
import { CachedServiceMapper } from '../../storage/mappers/cached-service-mapper';

/**
 * Service for getting all services associated with the current user.
 */
@Injectable({
	providedIn: 'root'
})
export class GetUserServicesQueryService {
	/** The mapper class to map cached data to and from stored models */
	private readonly cachedServiceMapper = new CachedServiceMapper();

	/** The mapper class for the dispatched service state data. */
	private readonly dispatchedServiceStateDataMapper: StateDataMapper = new StateDataMapper();

	/**
	 * @constructor
	 *
	 * @param getAllStateDataOperation The operation for getting all state data for the current user from the repository.
	 * @param getAllCachedServicesOperation Operation service responsible for retrieving all cached services from the repository.
	 */
	constructor(
		private readonly getAllStateDataOperation: GetAllStateDataOperation,
		private readonly getAllCachedServicesOperation: GetAllCachedServicesOperation
	) { }

	/**
	 * Retrieves dispatched services associated with a user.
	 * @returns An Observable emitting a list of dispatched service view models.
	 */
	public GetServices$(): Observable<DispatchedServiceViewModel[]> {
		return this.getAllCachedServicesOperation.Execute$().pipe(
			switchMap((cachedServices: StoredCachedService[]) => {
				return this.getAllStateDataOperation.Execute$().pipe(
					map((stateData: StoredStateData[]) => {
						if (cachedServices.length == 0 || stateData.length == 0) {
							return [];
						}

						const services: DispatchedServiceViewModel[] = stateData.map((serviceState: StoredStateData) => {
							const cachedService: StoredCachedService | null = cachedServices.find((service: StoredCachedService) => service.Id === serviceState.Id) ?? null;
							if (!cachedService)
								return null;

							const serviceViewModel: DispatchedServiceViewModel = this.cachedServiceMapper.FromStored(cachedService);
							return this.dispatchedServiceStateDataMapper.FromStored(serviceState, serviceViewModel);
						}).filter((service: DispatchedServiceViewModel | null): service is DispatchedServiceViewModel => service != null);

						return services;
					})
				);
			})
		);
	}
}
